import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import {
  Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, CloudFog, Cloudy,
  Newspaper, Maximize2, Minimize2, Megaphone, Volume2, VolumeX, Tv, Smartphone, Clock, Users, Building2, Info,
} from "lucide-react";
import { formatCode } from "../lib/ticketCode";
import { getQueueIcon, isCustomIcon } from "../lib/queueIcons";
import { getCommerceIcon, isCustomCommerceIcon } from "../lib/commerceIcons";

// Spec utilisateur fork 25.42 — palette des couleurs autorisées pour les
// messages personnalisés (par-file + global). 4 choix : bleu / rouge /
// noir / vert. Le défaut est `blue` côté backend.
const MSG_COLOR_HEX = { blue: "#002FA7", red: "#D72638", black: "#0A0A0A", green: "#0A7F2E" };
function pickMsgColor(id) { return MSG_COLOR_HEX[id] || MSG_COLOR_HEX.blue; }

// Spec utilisateur fork 25.42 — réduction automatique de la police pour
// que le message reste visible sans scroll sur l'écran public.
// Renvoie une taille en `clamp(min, vw_pct, max)` adaptée à la longueur.
function autoMsgFontSize(text, baseMin, baseVw, baseMax) {
  const n = (text || "").length;
  if (n <= 30) return `clamp(${baseMin}px, ${baseVw}vw, ${baseMax}px)`;
  if (n <= 60) return `clamp(${Math.max(10, baseMin - 1)}px, ${baseVw * 0.78}vw, ${baseMax * 0.78}px)`;
  return `clamp(${Math.max(9, baseMin - 2)}px, ${baseVw * 0.6}vw, ${baseMax * 0.62}px)`;
}

function weatherIcon(code) {
  if (code === 0) return { Icon: Sun, color: "text-[#FFB627]" };
  if ([1, 2].includes(code)) return { Icon: Cloudy, color: "text-[#9AA0A6]" };
  if (code === 3) return { Icon: Cloud, color: "text-[#9AA0A6]" };
  if ([45, 48].includes(code)) return { Icon: CloudFog, color: "text-[#9AA0A6]" };
  if ([51, 53, 55, 56, 57].includes(code)) return { Icon: CloudDrizzle, color: "text-[#4A90E2]" };
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return { Icon: CloudRain, color: "text-[#4A90E2]" };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { Icon: CloudSnow, color: "text-[#88BDE6]" };
  if ([95, 96, 99].includes(code)) return { Icon: CloudLightning, color: "text-[#A86DFF]" };
  return { Icon: Cloud, color: "text-white/70" };
}

const WEEKDAY_SHORT = ["Dim.", "Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam."];

export default function PublicDisplay() {
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const tvMode = searchParams.get("tv") === "1";
  const [data, setData] = useState(null);
  const [allQueues, setAllQueues] = useState([]);
  const [time, setTime] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [news, setNews] = useState([]);
  const [tvReady, setTvReady] = useState(!tvMode);
  const [audioOn, setAudioOn] = useState(false);
  const audioCtxRef = useRef(null);
  const prevCurrentByQueueRef = useRef({});

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) await document.documentElement.requestFullscreen({ navigationUI: "hide" });
      else await document.exitFullscreen();
    } catch (e) {
      console.warn("Fullscreen API unavailable:", e?.message);
    }
  };

  const ensureAudioCtx = () => {
    if (!audioCtxRef.current) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) audioCtxRef.current = new Ctx();
      } catch { /* ignored */ }
    }
    return audioCtxRef.current;
  };

  const playChime = () => {
    const ctx = audioCtxRef.current;
    if (!ctx || !audioOn) return;
    const now = ctx.currentTime;
    // Spec utilisateur Feb 2026 — carillon « bip-bip » deux tons distincts,
    // plus dissuasif (style SNCF / annonce aéroport « ding-dong »). Volume
    // augmenté, attaque plus marquée, deux notes nettement différentes :
    // - Bip 1 : E6 (1318 Hz)  → ton haut, court  (0,28 s)
    // - Pause silencieuse     :                   (0,08 s)
    // - Bip 2 : A5 (880 Hz)   → ton bas, soutenu (0,52 s)
    // L'enchaînement haut-bas est un signal d'appel universellement
    // reconnu (notifications iOS, annonces gares, hôpitaux).
    const notes = [
      { freq: 1318.51, start: now,        dur: 0.28, peak: 0.55 },
      { freq: 880.00,  start: now + 0.36, dur: 0.52, peak: 0.60 },
    ];
    notes.forEach(({ freq, start, dur, peak }) => {
      // Oscillateur principal (sine = clarté) + sous-oscillateur (triangle)
      // qui ajoute un peu de corps sans saturer. Compresseur léger sur la
      // chaîne pour mieux passer sur les enceintes TV petits formats.
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();
      osc1.type = "sine";
      osc2.type = "triangle";
      osc1.frequency.setValueAtTime(freq, start);
      osc2.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0.0001, start);
      // Attaque nette (10 ms) → pic → décroissance exponentielle.
      gain.gain.exponentialRampToValueAtTime(peak, start + 0.012);
      gain.gain.setValueAtTime(peak, start + dur * 0.35);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(ctx.destination);
      osc1.start(start);
      osc2.start(start);
      osc1.stop(start + dur + 0.05);
      osc2.stop(start + dur + 0.05);
    });
  };

  const activateTvMode = async () => {
    try {
      const ctx = ensureAudioCtx();
      if (ctx && ctx.state === "suspended") await ctx.resume();
      setAudioOn(true);
      if (!document.fullscreenElement) {
        try { await document.documentElement.requestFullscreen({ navigationUI: "hide" }); } catch { /* ignore */ }
      }
    } finally {
      setTvReady(true);
    }
  };

  const toggleAudio = async () => {
    if (audioOn) { setAudioOn(false); return; }
    const ctx = ensureAudioCtx();
    if (ctx && ctx.state === "suspended") { try { await ctx.resume(); } catch { /* ignore */ } }
    setAudioOn(true);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/public/display/${id}`);
        setData(res.data);
      } catch { /* keep last state */ }
      try {
        const qres = await api.get(`/commerces/${id}/queues`);
        setAllQueues(qres.data || []);
      } catch { /* keep last state */ }
    };
    load();
    const i = setInterval(load, 2000);
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => { clearInterval(i); clearInterval(t); };
  }, [id]);

  useEffect(() => {
    // Weather is now served by our backend proxy (1h cache + server-side
    // geocoding). This avoids CORS/proxy/ad-blocker issues that were
    // hiding the weather strip on some networks.
    const load = async () => {
      try {
        const r = await api.get(`/public/weather/${id}`);
        setWeather(r.data);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30 * 60 * 1000); // 30 min refresh client-side
    return () => clearInterval(t);
  }, [id]);

  useEffect(() => {
    const loadNews = async () => {
      try {
        const { data: n } = await api.get("/public/news-ticker");
        setNews(n.items || []);
      } catch { /* ignore */ }
    };
    loadNews();
    const t = setInterval(loadNews, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Chime trigger when a queue's currentCode changes between polls
  useEffect(() => {
    const next = {};
    let triggered = false;
    allQueues.forEach((q) => {
      const code = q.currentCode || (q.currentNumber != null && q.letter ? `${q.letter}${String(q.currentNumber).padStart(3, "0")}` : q.currentNumber);
      next[q.id] = code || null;
      const prev = prevCurrentByQueueRef.current[q.id];
      if (Object.keys(prevCurrentByQueueRef.current).length > 0 && code && code !== prev) triggered = true;
    });
    if (triggered) playChime();
    prevCurrentByQueueRef.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allQueues]);

  if (!data) return <div className="w-screen h-screen flex items-center justify-center bg-[#0A1A4F] text-white">…</div>;

  const visibleQueues = allQueues.filter((q) => q.displayOnPublic !== false);
  const N = visibleQueues.length;
  // Layout adaptatif (spec utilisateur 2026-02 — ticket digital, 1→10 files) :
  //   SUR MOBILE → 2 cartes par ligne (sauf 1 file unique : centrée).
  //   Au-delà de sm/md/lg/xl on densifie la grille selon le nombre de files.
  const colsClass =
    N <= 1 ? "grid-cols-1 place-content-center" :
    N === 2 ? "grid-cols-2" :
    N <= 4 ? "grid-cols-2" :
    N <= 6 ? "grid-cols-2 md:grid-cols-3" :
    N <= 8 ? "grid-cols-2 md:grid-cols-4" :
    "grid-cols-2 md:grid-cols-3 lg:grid-cols-5";
  const tickerItems = news.length ? news : [{ title: "Chargement de l'actualité…" }];
  const globalQrUrl = `${apiBase}/commerces/${id}/global-qr.png`;
  const CommerceIcon = getCommerceIcon(data.icon);
  const customCommerceIcon = isCustomCommerceIcon(data.icon) ? data.icon : null;

  // Establishment slogan / subtitle — derived from public commerce data if present
  const subtitle = data.subtitle || data.tagline || "";

  return (
    <div className="w-screen min-h-screen md:h-screen flex flex-col bg-[#0F172A] text-white md:overflow-hidden">
      {!tvReady && (
        <div className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-sm flex items-center justify-center px-6" data-testid="tv-mirror-activation-overlay">
          <div className="max-w-md w-full bg-white text-ink rounded-2xl border-2 border-brand p-6 sm:p-8 text-center shadow-2xl">
            <span aria-hidden="true" className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand/10 text-brand mb-3"><Tv size={32} /></span>
            <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight">Mode TV Miroir</h2>
            <p className="mt-3 text-sm text-subtle leading-relaxed">Cliquez pour activer le <span className="font-bold text-ink">plein écran automatique</span> et le <span className="font-bold text-ink">carillon sonore</span> à chaque appel de ticket.</p>
            <button type="button" onClick={activateTvMode} className="mt-5 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-brand text-white hover:bg-brand-hover font-display font-black uppercase tracking-[0.1em] text-sm shadow-md" data-testid="tv-mirror-activate-btn">
              <Volume2 size={16} /> Activer la TV
            </button>
            <p className="mt-3 text-[11px] text-subtle">Astuce — appuyez sur <kbd className="px-1 border border-border rounded text-[10px]">Esc</kbd> pour sortir du plein écran.</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bpg-ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .bpg-ticker-track { display: inline-flex; white-space: nowrap; animation: bpg-ticker 220s linear infinite; will-change: transform; }
        @keyframes bpg-queue-msg-blink-kf { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
        .bpg-queue-msg-blink { animation: bpg-queue-msg-blink-kf 1s steps(1, end) infinite; }

        /* Spec utilisateur Feb 2026 — texte « File suspendue » discret
           sous le numéro de ticket. Clignotement doux toutes les 2 s
           (1,2 s visible / 0,8 s atténué) — jamais entièrement invisible
           pour rester lisible sur TV. */
        @keyframes bpg-suspended-2s-kf {
          0%, 60% { opacity: 1; }
          80%     { opacity: 0.25; }
          100%    { opacity: 1; }
        }
        .bpg-suspended-2s { animation: bpg-suspended-2s-kf 2s ease-in-out infinite; }

        /* Spec utilisateur fork 25.31 — bandeau d'ouverture programmée
           sur l'écran public : cycle de 3 s avec ~2 s visibles + ~1 s
           atténué (au lieu d'un flash binaire) pour laisser le temps
           de lire le message complet. */
        @keyframes bpg-schedule-pulse-kf {
          0%, 70% { opacity: 1; }
          85% { opacity: 0.25; }
          100% { opacity: 1; }
        }
        .bpg-schedule-pulse { animation: bpg-schedule-pulse-kf 3s ease-in-out infinite; }
      `}</style>

      {/* ============ HEADER (white band — BypassGo + clock, bold for contrast) ============ */}
      <header className="grid grid-cols-[auto_1fr_auto] items-center gap-2 md:gap-5 px-3 md:px-6 py-2 md:py-3 bg-white text-ink border-b border-[#E7EAF1] shadow-sm shrink-0">
        {/* BypassGo brand */}
        <div className="flex items-center gap-2" data-testid="display-bypassgo-brand">
          <img src="/logo-bypassgo-b-only.png" alt="BypassGo" className="object-contain h-10 md:h-14" />
          <div>
            <p className="font-display font-black tracking-tight leading-none text-base md:text-2xl">
              <span className="text-brand">Bypass</span><span className="text-[#0A7F2E]">Go</span>
            </p>
            <p className="text-[8px] md:text-[11px] uppercase tracking-[0.14em] text-ink/65 leading-tight mt-0.5 font-bold" data-testid="display-bypassgo-slogan">
              La file d'attente<br />intelligente
            </p>
          </div>
        </div>

        {/* Weather strip (7 days) — desktop only, centered.
           Spec utilisateur Feb 2026 — version XL pour TV : icônes plus
           grandes, jours et températures en gras + en couleur, occupe
           la zone blanche disponible entre le logo et l'horloge. */}
        {weather?.daily ? (
          <div className="hidden md:flex items-center justify-center gap-4 lg:gap-6 xl:gap-7" data-testid="weather-forecast">
            {weather.daily.time.slice(0, 7).map((d, i) => {
              const code = weather.daily.weather_code[i];
              const tmax = Math.round(weather.daily.temperature_2m_max[i]);
              const tmin = Math.round(weather.daily.temperature_2m_min[i]);
              const W = weatherIcon(code);
              const dow = WEEKDAY_SHORT[new Date(d).getDay()];
              return (
                <div
                  key={d}
                  className="flex flex-col items-center min-w-[56px] lg:min-w-[64px] xl:min-w-[72px]"
                  data-testid={`weather-day-${i}`}
                >
                  <p className="text-xs lg:text-sm xl:text-base uppercase tracking-wider font-black text-[#1D2D7B]">
                    {i === 0 ? "Auj." : dow}
                  </p>
                  <W.Icon size={28} className={`${W.color} my-0.5 lg:my-1 drop-shadow-sm`} strokeWidth={2.4} />
                  <p className="font-mono text-sm lg:text-base xl:text-lg font-black leading-none">
                    <span className="text-[#D7263D]">{tmax}°</span>
                    <span className="text-[#1B5BFF] ml-1">{tmin}°</span>
                  </p>
                </div>
              );
            })}
          </div>
        ) : <div aria-hidden="true" />}

        {/* Clock + date + controls (bold black on white) */}
        <div className="flex items-center gap-1.5 md:gap-2 shrink-0 justify-self-end">
          <div className="text-right">
            <p className="font-mono text-xl md:text-4xl font-black leading-none text-ink tracking-tight" data-testid="display-clock">{time.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
            <p className="text-[9px] md:text-xs text-ink/70 capitalize mt-0.5 font-semibold">{time.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</p>
          </div>
          <button type="button" onClick={toggleAudio} className={`hidden sm:inline-flex items-center justify-center w-9 h-9 rounded ${audioOn ? "bg-brand text-white" : "border border-[#D7DBE5] text-ink/70 hover:border-ink hover:text-ink"}`}
            title={audioOn ? "Couper le son" : "Activer le carillon"} data-testid="display-audio-btn"
          >{audioOn ? <Volume2 size={16} /> : <VolumeX size={16} />}</button>
          <button type="button" onClick={toggleFullscreen} className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded border border-[#D7DBE5] text-ink/70 hover:border-ink hover:text-ink" title={isFullscreen ? "Quitter le plein écran (Esc)" : "Plein écran"} data-testid="display-fullscreen-btn">
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      </header>

      {/* ============ ESTABLISHMENT NAMEPLATE ============
         Premium isolated frame, centered, with icon + full bold name.
         Wraps on small screens, never truncated. */}
      <div className="flex justify-center px-3 md:px-6 pt-3 md:pt-5 pb-1 md:pb-2 shrink-0" data-testid="display-commerce-nameplate">
        <div className="inline-flex items-center gap-3 md:gap-5 max-w-[96%] md:max-w-[85%] bg-white/10 backdrop-blur-sm border-2 border-white/40 rounded-2xl px-4 md:px-8 py-2.5 md:py-4 shadow-[0_8px_28px_-12px_rgba(0,0,0,0.5)]">
          <span
            aria-hidden="true"
            className="shrink-0 inline-flex items-center justify-center w-11 h-11 md:w-16 md:h-16 rounded-xl bg-white text-brand overflow-hidden ring-2 ring-white/60"
            data-testid="display-commerce-icon"
          >
            {customCommerceIcon ? (
              <img src={customCommerceIcon} alt="" className="w-full h-full object-contain p-1" />
            ) : data.hasLogo ? (
              <img
                src={`${apiBase}/commerces/${id}/logo${data.logoUpdatedAt ? `?v=${encodeURIComponent(data.logoUpdatedAt)}` : ""}`}
                alt="" className="w-full h-full object-contain p-1"
                data-testid="display-commerce-logo"
              />
            ) : (() => {
              const I = CommerceIcon || Building2;
              return <I className="w-7 h-7 md:w-10 md:h-10" strokeWidth={2.2} />;
            })()}
          </span>
          <div className="min-w-0 text-center md:text-left">
            <h1
              className="font-display text-2xl md:text-5xl font-black uppercase tracking-tight text-white leading-[1.05] break-words"
              data-testid="display-commerce-name"
            >
              {data.commerceName}
            </h1>
            {subtitle ? (
              <p className="mt-1 text-xs md:text-sm text-[#BFD2FF] font-medium tracking-wide truncate md:whitespace-normal" data-testid="display-commerce-subtitle">{subtitle}</p>
            ) : null}
          </div>
        </div>
      </div>

      {/* WEATHER ROW (mobile only) */}
      {weather?.daily ? (
        <div className="md:hidden flex items-center justify-between gap-1 px-2 py-1.5 border-b border-white/10 overflow-x-auto" data-testid="weather-forecast-mobile">
          {weather.daily.time.slice(0, 7).map((d, i) => {
            const code = weather.daily.weather_code[i];
            const tmax = Math.round(weather.daily.temperature_2m_max[i]);
            const tmin = Math.round(weather.daily.temperature_2m_min[i]);
            const W = weatherIcon(code);
            const dow = WEEKDAY_SHORT[new Date(d).getDay()];
            return (
              <div key={d} className="flex flex-col items-center shrink-0 px-1" data-testid={`weather-day-mobile-${i}`}>
                <p className="text-[9px] uppercase tracking-wider text-white/70 font-bold leading-none">{i === 0 ? "Auj." : dow}</p>
                <W.Icon size={14} className={`${W.color} my-0.5`} />
                <p className="font-mono text-[10px] font-bold leading-none">
                  <span className="text-white">{tmax}°</span><span className="text-white/60 ml-0.5">{tmin}°</span>
                </p>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* ============ GLOBAL MESSAGE BANNER (clean: text only, no title/icon) ============ */}
      {data.globalMessage ? (
        <div className="bg-white mx-3 md:mx-6 mt-3 mb-2 rounded-lg px-4 md:px-6 py-2.5 md:py-3 shadow-sm" data-testid="display-global-message">
          <p
            className="font-display font-bold leading-snug break-words text-center"
            style={{
              color: pickMsgColor(data.globalMessageColor),
              fontSize: autoMsgFontSize(data.globalMessage, 14, 1.5, 24),
            }}
          >
            {data.globalMessage}
          </p>
        </div>
      ) : null}

      {/* ============ MAIN GRID ============
         `auto-rows-fr` et `flex-1` ne s'appliquent qu'à partir de md: pour
         que sur mobile les cartes gardent une hauteur naturelle (évite
         l'étirement vertical qui faisait déborder le gros code de ticket
         et masquait les indicateurs). */}
      <main className="flex-1 flex flex-col px-3 md:px-6 pb-3 min-h-0">
        {N > 0 ? (
          <div className={`grid ${colsClass} md:auto-rows-fr gap-2 sm:gap-3 md:gap-5 min-h-0 md:flex-1`} data-testid="display-queues-grid">
            {visibleQueues.map((q) => (
              <PublicQueueCard
                key={q.id}
                queue={q}
                single={N === 1}
                count={N}
              />
            ))}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-white/60 text-xl">Aucune file configurée.</div>
        )}

        {/* ============ GLOBAL QR ROW — large central QR ============
           Spec utilisateur Feb 2026 — la carte du QR reprend le fond
           blanc des tickets, mais elle ne s'étire plus sur toute la
           largeur de l'écran : `mx-auto` + `max-w-…` la cadrent au
           centre, occupant juste la place nécessaire (mobile ≈ 90%,
           desktop ≈ 720 px max). */}
        {N > 0 ? (
          <section
            className="mt-2 md:mt-3 mx-auto w-full max-w-[480px] md:max-w-[640px] lg:max-w-[720px] rounded-2xl bg-white shadow-[0_10px_28px_-10px_rgba(0,0,0,0.45)] ring-1 ring-black/5 px-4 md:px-6 py-4 md:py-5 flex flex-col items-center justify-center gap-3 md:gap-4 shrink-0"
            data-testid="display-global-qr-block"
          >
            <div className="bg-white p-2 md:p-3 rounded-lg ring-2 md:ring-4 ring-[#BFD2FF]/60">
              <img
                src={globalQrUrl}
                alt="QR global"
                style={{ width: "clamp(140px, 18vh, 240px)", height: "clamp(140px, 18vh, 240px)" }}
                data-testid="display-global-qr"
              />
            </div>
            <p
              className="font-display font-bold text-ink text-xs md:text-xl text-center flex items-center gap-2 flex-wrap justify-center px-2"
              data-testid="display-global-qr-message"
            >
              <span aria-hidden="true" className="inline-flex items-center justify-center w-7 h-7 md:w-10 md:h-10 rounded-full bg-[#EAF1FF] text-[#1B5BFF]">
                <Smartphone size={16} />
              </span>
              <span>Scannez votre QR code et choisissez votre file d'attente.</span>
            </p>
          </section>
        ) : null}
      </main>

      {/* ============ FRANCE INFO TICKER ============
         Spec utilisateur Feb 2026 — texte plus gros et plus gras pour
         être lisible de loin sur une TV. */}
      <div className="border-t border-white/10 bg-white text-ink overflow-hidden shrink-0" data-testid="news-ticker">
        <div className="flex items-stretch">
          <div className="shrink-0 bg-[#FF3B30] text-white px-3 md:px-5 py-2 md:py-3 flex items-center gap-2 md:gap-2.5 font-display font-black text-sm md:text-lg lg:text-xl uppercase tracking-wider">
            <Newspaper size={18} className="md:hidden" />
            <Newspaper size={22} className="hidden md:inline" />
            Actualités
          </div>
          <div className="relative flex-1 overflow-hidden py-2 md:py-3">
            <div className="bpg-ticker-track text-base md:text-xl lg:text-2xl font-black text-ink" data-testid="news-ticker-track">
              {[...tickerItems, ...tickerItems].map((n, idx) => (
                <span key={idx} className="bpg-ticker-item mr-10">{n.title}<span className="mx-4 text-[#FF3B30]">●</span></span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Carte "ticket digital" — design unifié 1 → 10 files.
 *
 * Structure (inspirée d'un ticket de cinéma / coupon) :
 *   ┌──────┬──────────────────────────────┐
 *   │      │ • NOM FILE                   │
 *   │  B   │                              │
 *   │      │        C018  ← gros numéro   │
 *   │ stub │                              │
 *   │      │ [ EN COURS ]                 │
 *   │      │ ──────────────────────────── │
 *   │      │ 👥 2 personnes  ⏰ 5 min     │
 *   └──────┴──────────────────────────────┘
 *           ▲                ▲
 *      encoche haut    encoche bas (cercles du bg du parent
 *                              qui découpent la jointure → effet ticket)
 *
 * Scaling : 6 paliers de densité selon `count`, indicateurs et numéro restent
 * lisibles quelle que soit la grille (jusqu'à 5 colonnes pour 9-10 files).
 */
function PublicQueueCard({ queue, single, count = 1 }) {
  const open = !!queue.isOpen;
  const suspended = !!queue.isSuspended;
  const color = queue.color || "#1B5BFF";
  const currentCode = queue.currentCode || formatCode(queue.letter, queue.currentNumber) || (queue.letter ? `${queue.letter}000` : "—");
  const waiting = queue.waitingCount ?? 0;
  const avg = queue.avgWaitMinutes;

  // Spec utilisateur fork 25.30 — bandeau rouge "OUVERTURE PROGRAMMÉE"
  // visible côté public quand la file est fermée mais possède une
  // `scheduledOpenAt` future. Applicable à TOUS les comptes (existants
  // et futurs). Si la date est dans le passé (le sweep n'a pas encore
  // ré-ouvert), on n'affiche rien — c'est une question de secondes.
  const scheduledLabel = (() => {
    if (open) return null;
    if (!queue.scheduledOpenAt) return null;
    const ts = new Date(queue.scheduledOpenAt).getTime();
    if (!Number.isFinite(ts) || ts <= Date.now()) return null;
    try {
      return new Date(queue.scheduledOpenAt).toLocaleString("fr-FR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  })();

  // Un ticket est "en cours" quand un numéro a effectivement été appelé
  // (sinon `currentNumber` reste à 0 ou null → on est "en attente" d'un appel).
  const hasActiveCall = !!(queue.currentNumber && queue.currentNumber > 0);
  const statusLabel = suspended ? "FILE SUSPENDUE" : !open ? "Fermée" : hasActiveCall ? "En cours" : "En attente";
  const statusColor = suspended ? "#F18F18" : !open ? "#DC3545" : color;

  // Paliers de densité (réajustés pour le mode 2-col mobile)
  const dense = count >= 5;       // 5+ files : compact
  const veryDense = count >= 9;   // 9-10 files : très compact

  // Largeur de la "stub" — fortement réduite sur mobile pour libérer un
  // maximum de largeur à la zone blanche (nom + numéro + indicateurs).
  const stubWidth = single ? 130 : veryDense ? 36 : dense ? 40 : 44;

  // Taille du gros code de ticket (clamp adaptatif).
  // Bornes mini RÉDUITES sur mobile : avec un code à 4-6 caractères (CODE,
  // COOCOU, GOOSE…), une borne min trop élevée (40 px) déborde la carte
  // mobile en 2-col. On laisse vw piloter avec une min ≤ 24 px.
  // Spec utilisateur Feb 2026 — tailles de numéro recalibrées pour que le
  // chiffre RESTE TOUJOURS dans les limites de sa carte, quelle que soit la
  // hauteur disponible (TV avec gros header météo + nameplate + ticker).
  // Le précédent palier 200 px en `single` débordait dans le footer et
  // créait le « trait blanc qui coupe le numéro » signalé par l'utilisateur.
  const codeFontSize =
    single ? "clamp(56px, 8vw, 132px)" :
    count === 2 ? "clamp(32px, 7vw, 116px)" :
    count <= 4 ? "clamp(28px, 6vw, 96px)" :
    count <= 6 ? "clamp(24px, 4.6vw, 80px)" :
    count <= 8 ? "clamp(22px, 3.6vw, 60px)" :
                 "clamp(20px, 3vw, 48px)";

  // Tailles annexes — pilotées par les flags de densité
  // Spec utilisateur Feb 2026 — tailles agrandies pour les écrans TV :
  // nom de file, compteurs (personnes + durée) et icônes plus visibles
  // depuis l'autre bout de la pièce.
  const nameSizeCls = veryDense ? "text-xs md:text-base" : dense ? "text-sm md:text-lg" : "text-base md:text-2xl lg:text-3xl";
  const statusSizeCls = veryDense ? "text-[8px] md:text-[10px] px-1.5 py-0.5" : dense ? "text-[9px] md:text-[11px] px-2 py-0.5" : "text-[10px] md:text-sm px-2.5 py-1";
  const indicatorTextCls = veryDense ? "text-xs md:text-base" : dense ? "text-sm md:text-lg" : "text-base md:text-2xl lg:text-3xl";
  const indicatorIconSize = veryDense ? 13 : dense ? 17 : 26;

  return (
    <div
      className={`relative h-full min-h-0 ${single ? "max-w-2xl mx-auto w-full" : ""}`}
      data-testid={`display-queue-card-${queue.id}`}
    >
      {/* Corps du ticket : stub colorée + zone blanche */}
      <div className="flex h-full rounded-2xl overflow-hidden shadow-[0_10px_28px_-10px_rgba(0,0,0,0.45)] ring-1 ring-black/5 bg-white">
        {/* ── STUB (bande latérale colorée + logo B BypassGo) ── */}
        <div
          className="shrink-0 flex items-center justify-center relative"
          style={{
            width: `${stubWidth}px`,
            background: `linear-gradient(135deg, ${color} 0%, ${color}E6 60%, ${color}CC 100%)`,
          }}
          data-testid={`display-queue-stub-${queue.id}`}
        >
          <img
            src="/logo-bypassgo-b-only.png"
            alt=""
            className="object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.25)] select-none pointer-events-none"
            style={{ width: "72%", maxWidth: "120px", maxHeight: "70%" }}
          />
        </div>

        {/* ── ZONE PRINCIPALE BLANCHE ── */}
        <div className="flex-1 min-w-0 flex flex-col p-3 md:p-5">
          {/* Header : pastille colorée + nom de la file */}
          <div className="flex items-center gap-2 md:gap-2.5 shrink-0">
            <span
              className="inline-block rounded-full shrink-0"
              style={{ backgroundColor: color, width: veryDense ? 8 : 10, height: veryDense ? 8 : 10 }}
              aria-hidden="true"
            />
            <h3
              className={`font-display font-black uppercase tracking-wide line-clamp-2 md:line-clamp-1 break-words ${nameSizeCls}`}
              style={{ color }}
              data-testid={`display-queue-name-${queue.id}`}
            >
              {queue.name}
            </h3>
          </div>

          {/* Numéro central — Spec utilisateur Feb 2026 :
             - Centré horizontalement ET verticalement (justify-center +
               items-center) dans la zone restante de la carte.
             - `overflow-hidden` empêche tout débordement vers le footer
               qui causait précédemment le « trait blanc qui coupe le
               numéro » et le chevauchement avec la pastille FILE
               SUSPENDUE.
             - `leading-[0.85]` réduit légèrement la hauteur visuelle de
               la police pour garder de l'air autour du chiffre. */}
          <div className="md:flex-1 min-h-0 w-full flex items-center justify-center py-2 md:py-2 overflow-hidden">
            <p
              className="font-display font-black tracking-tight whitespace-nowrap text-center"
              style={{ color, fontSize: codeFontSize, lineHeight: 0.88 }}
              data-testid={`display-queue-current-${queue.id}`}
            >
              {currentCode}
            </p>
          </div>

          {/* Pastille statut — Spec utilisateur Feb 2026 : la pastille
             standard (EN COURS / EN ATTENTE / FERMÉE) reste à sa place
             usuelle sous le numéro. Quand la file est SUSPENDUE, on
             masque cette zone et on place la mention « FILE SUSPENDUE »
             au centre du footer (entre 0 personnes et la durée estimée),
             pour ne jamais chevaucher le numéro de ticket. */}
          {!suspended && (
            <div className="shrink-0">
              <span
                className={`inline-flex items-center justify-center rounded-md text-white font-display font-black uppercase tracking-wider whitespace-nowrap ${statusSizeCls}`}
                style={{ backgroundColor: statusColor }}
                data-testid={`display-queue-status-${queue.id}`}
              >
                {statusLabel}
              </span>
            </div>
          )}

          {/* Spec utilisateur fork 25.30 — bandeau rouge clignotant
             "Ouverture programmée le …" affiché côté public visiteur
             quand la file est fermée ET qu'une `scheduledOpenAt` est
             prévue dans le futur. Applicable à TOUS les comptes. */}
          {scheduledLabel && (
            <div
              className="mt-2 md:mt-3 px-3 py-1.5 md:py-2 rounded-md bg-destructive text-white font-display font-black text-center uppercase tracking-wider leading-tight shrink-0 bpg-schedule-pulse"
              data-testid={`display-queue-scheduled-${queue.id}`}
              style={{ fontSize: "clamp(11px, 1.4vw, 16px)" }}
            >
              Ouverture programmée le {scheduledLabel}
            </div>
          )}

          {/* Message personnalisé : uniquement quand une seule file.
             Spec utilisateur Feb 2026 — bug corrigé : la couleur choisie
             par le marchand (bleu/rouge/noir/vert) est désormais appliquée
             au texte. Auparavant le className `text-ink` forçait le noir. */}
          {single && queue.customMessage ? (() => {
            const msgColorMap = {
              blue:  "#1B5BFF",
              red:   "#D7263D",
              green: "#0A7F2E",
              black: "#0A0F1E",
            };
            const msgColor = msgColorMap[queue.customMessageColor] || msgColorMap.blue;
            return (
              <div
                className={`mt-2 md:mt-3 px-3 py-2 rounded-md border-l-4 border-r-4 bg-surface text-sm md:text-base font-black leading-snug break-words text-center shrink-0 ${queue.customMessageBlink !== false ? "bpg-queue-msg-blink" : ""}`}
                style={{ borderLeftColor: color, borderRightColor: color, color: msgColor }}
                data-testid={`display-queue-custom-msg-${queue.id}`}
              >
                {queue.customMessage}
              </div>
            );
          })() : null}

          {/* Indicateurs : 👥 X  |  ⏰ X min
             Sur mobile (< md) on n'affiche QUE les chiffres + icônes pour
             garantir que les 2 indicateurs tiennent côte à côte sans
             troncature dans une carte étroite. Le texte complet (« 7
             personnes » / « 12 min ») réapparaît dès md:. */}
          <div className={`flex items-center justify-between ${veryDense ? "gap-1" : dense ? "gap-1.5" : "gap-2 md:gap-5"} mt-2 md:mt-3 pt-2 md:pt-3 border-t border-[#E7EAF1] shrink-0`}>
            <div
              className="flex items-center gap-1 md:gap-2 min-w-0"
              data-testid={`display-queue-waiting-${queue.id}`}
            >
              <Users size={indicatorIconSize} className="text-ink/60 shrink-0" strokeWidth={2.4} />
              <p className={`font-display text-ink whitespace-nowrap ${indicatorTextCls}`}>
                <span className="font-black">{waiting}</span>
                <span className="font-bold ml-1 hidden md:inline">{waiting === 1 ? "personne" : "personnes"}</span>
              </p>
            </div>
            {/* Spec utilisateur Feb 2026 — bandeau central « FILE SUSPENDUE »
               EXACTEMENT entre l'indicateur 👥 et l'indicateur ⏰ quand la
               file est suspendue. Police agrandie, clignotement 2 s,
               n'écrase jamais le numéro de ticket (qui est plus haut).
               Quand la file n'est pas suspendue, on garde le séparateur
               vertical d'origine pour conserver le rendu habituel. */}
            {suspended ? (
              <div className="flex-1 flex justify-center px-1 md:px-2 min-w-0">
                <span
                  className="inline-flex items-center justify-center rounded-md text-white font-display font-black uppercase tracking-wider whitespace-nowrap bpg-suspended-2s px-3 md:px-4 py-1 md:py-1.5 text-sm md:text-xl lg:text-2xl"
                  style={{ backgroundColor: "#F18F18", letterSpacing: "0.05em" }}
                  data-testid={`display-queue-status-${queue.id}`}
                >
                  FILE SUSPENDUE
                </span>
              </div>
            ) : (
              <span className="text-ink/25 font-bold shrink-0 hidden md:inline" aria-hidden="true">|</span>
            )}
            <div
              className="flex items-center gap-1 md:gap-2 min-w-0"
              data-testid={`display-queue-avg-${queue.id}`}
            >
              <Clock size={indicatorIconSize} className="text-ink/60 shrink-0" strokeWidth={2.4} />
              <p className={`font-display text-ink whitespace-nowrap ${indicatorTextCls}`}>
                {avg != null ? (
                  <>
                    <span className="font-black">{avg}</span>
                    <span className="font-bold ml-1">min</span>
                  </>
                ) : (
                  <span className="text-ink/40 font-bold">—</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── ENCOCHES TICKET (cercles du bg parent à la jointure stub/main) ── */}
      <span
        className="absolute rounded-full bg-[#0F172A] z-10 pointer-events-none"
        style={{
          width: veryDense ? 12 : 16,
          height: veryDense ? 12 : 16,
          top: veryDense ? -6 : -8,
          left: stubWidth - (veryDense ? 6 : 8),
        }}
        aria-hidden="true"
      />
      <span
        className="absolute rounded-full bg-[#0F172A] z-10 pointer-events-none"
        style={{
          width: veryDense ? 12 : 16,
          height: veryDense ? 12 : 16,
          bottom: veryDense ? -6 : -8,
          left: stubWidth - (veryDense ? 6 : 8),
        }}
        aria-hidden="true"
      />
    </div>
  );
}
